import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  growthFactor,
} from '../sharedPolicyConfig';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';

// ---------------------------------------------------------------------------
// Life Insurance with LTC Rider — a single death-benefit pool accelerated to
// pay for long-term care. One strategy serves all three single-life policy
// types whose `calculationType === lifeInsuranceWithRider`:
//   - `lifeInsuranceWithRider` (generic "Life with Benefits Rider")
//   - `lifeWithIndexAccount`   (Transamerica Financial Foundation IUL)
//   - `noLapseGuaranteeLife`   (Nationwide No-Lapse Guarantee UL II)
//
// SINGLE ACCELERATED POOL (the death benefit IS the pool):
// - `policyMaximumBenefitAmount` is the Specified Amount / face — both the
//   single accelerable LTC pool AND the death benefit. LTC draws reduce the
//   death benefit dollar-for-dollar. There is NO continuation-of-benefits
//   pool, so `extensionOfBenefitsPool = 0` on every row and the `eob` flow is
//   always `{ depletion: 0, inflationAccrual: 0 }`. This matches the legacy
//   single-pool routing — `isHybridPolicy(lifeInsuranceWithRider) === false`,
//   so `calcPolicyTotalLtcLimitForPhase` uses `calculateStandardPolicyBenefit`
//   (single pool), not the two-pool hybrid path.
//
// DEATH-BENEFIT FLOOR (residual death benefit):
// - Residual DB floored at `policyMinimumGuaranteedBenefitAmount` (e.g.
//   Nationwide's 10% Residual Death Benefit = $40,000 on $400,000 face). DB
//   opens NET OF prior utilization — the single pool IS the DB, so a partially
//   used policy opens with the residual already reduced.
//
// SPLIT INFLATION RATES (reviewer correction):
// - The DB inflates (none of these three are in `hasFlatDeathBenefit`).
// - Pool/cap inflate on `policyInflationProtection`.
// - The death benefit inflates on
//   `policyInflationProtectionOnFaceAmount ?? policyInflationProtection`.
// - These coincide only when the face-amount knob is unset or equals the
//   policy-wide rate; they diverge at rate > 0 when set separately. Both
//   simple and compound inflation are supported (`isSimpleInflationProtection`).
// - The sample NLG UL II illustration elects 0% inflation, so all factors are
//   1.0 there (split-rate divergence is invisible at the 0% boundary).
//
// PAYOUT MODEL — reimbursement (default) OR cash indemnity:
// - Reimbursement: pay `min(eligibleCost, proRatedCap)` from the single pool.
// - Indemnity (gated on `isIndemnityPolicyPayment`): pay the full pro-rated
//   monthly cap as cash regardless of actual cost, drawn from the pool, and
//   accrue any excess over eligible cost as `indemnitySurplus` (drained on a
//   later shortfall month — `applyIndemnitySurplus`, the same helper CM II
//   uses). NLG UL II is an INDEMNITY product ("Cash Indemnity Benefits ... no
//   bills or receipts are needed", illustration p.8), so the parity baseline
//   for that product runs with `isIndemnityPolicyPayment: true`.
//
// ELIMINATION PERIOD: standard two-tier (home/facility) WP; EP days are strictly
// out-of-pocket — these products are not in
// `defaultsRetroactiveWaitingPeriodReimbursement`, so there is NO retroactive
// lump. Standard 2-of-6-ADL (or severe cognitive impairment) gate.
//
// STRATEGY STATE: carries `indemnitySurplus` (the indemnity reserve). Deltas
// surface the per-row surplus accrual/application for the UI.
// ---------------------------------------------------------------------------

export interface LifeWithRiderState {
  // Indemnity-surplus reserve: indemnity dollars paid above eligible cost,
  // carried forward and drained on a later shortfall month. Always 0 for a
  // reimbursement policy.
  indemnitySurplus: number;
}

export interface LifeWithRiderDeltas {
  // Indemnity payout above this month's eligible cost, added to the reserve.
  indemnitySurplusAccrued: number;
  // Reserve dollars drained to cover this month's eligible-cost shortfall.
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  // Pool / monthly-cap rate (legacy: `policyInflationProtection`).
  poolRate: number;
  // Death-benefit rate (legacy:
  // `policyInflationProtectionOnFaceAmount ?? policyInflationProtection`).
  dbRate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    poolRate: policyWide,
    dbRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

function getFaceAtIssue(ltcPolicy: PolicyFundingSource): number {
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

// Legacy/CCRC-created life-with-rider rows can omit all four daily benefit
// fields. In that no-cap shape the legacy engine paid UNCAPPED — a missing cap
// must not collapse every payout to $0 (the sparse-import fallback resolves
// to a $0 max when nothing is populated).
function hasConfiguredMonthlyCap(ltcPolicy: PolicyFundingSource): boolean {
  return [
    ltcPolicy.dailyHomeBenefitAmount,
    ltcPolicy.dailyAssistedLivingBenefitAmount,
    ltcPolicy.dailyNursingHomeBenefitAmount,
    ltcPolicy.dailyIndependentLivingBenefitAmount,
  ].some(value => value != null);
}

export const lifeWithRiderStrategy: PolicyProjectionStrategy<
  LifeWithRiderState,
  LifeWithRiderDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<LifeWithRiderState> {
    const faceAtIssue = getFaceAtIssue(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { poolRate, dbRate, capYears, isSimple } = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated (the
    // projection only spans claim months), so pre-inflate to the value at
    // claim start. A claim starting in policy year 1 keeps the factors at 1.0;
    // on the 0%-inflation NLG illustration both factors are always 1.0. The
    // pool and DB use their own rates (split-rate correction).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart =
      faceAtIssue * growthFactor(poolRate, capYears, isSimple, claimStartPolicyYear);
    const dbAtClaimStart =
      faceAtIssue * growthFactor(dbRate, capYears, isSimple, claimStartPolicyYear);
    return {
      pools: {
        // The single accelerable pool = the LTC capacity (net of utilization).
        baseBenefitPool: Math.max(poolAtClaimStart - utilizedToDate, 0),
        // No continuation-of-benefits pool, ever.
        extensionOfBenefitsPool: 0,
        // The residual DB opens at the current accelerable face net of
        // utilization, floored — a partially used policy opens reduced.
        deathBenefit: Math.max(dbAtClaimStart - utilizedToDate, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { poolRate, capYears, isSimple } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const faceAtIssue = getFaceAtIssue(ltcPolicy);
    // Read the payment model from the policy — never hard-coded. NLG UL II is
    // indemnity; generic life-with-rider defaults to reimbursement.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted from the single accelerable pool at claim
    // start, capped at the pre-inflated pool (mirrors `initClaimState`'s
    // `Math.max(poolAtClaimStart − utilized, 0)`). The death-benefit reduction
    // is a separate concern and does not feed "Drawn to date".
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const poolAtClaimStart =
      faceAtIssue * growthFactor(poolRate, capYears, isSimple, claimStartPolicyYear);
    const priorUtilization = Math.min(ltcPolicy.policyBenefitUtilizedToDate ?? 0, poolAtClaimStart);
    return {
      strategyId: 'life-with-rider',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish); the helper honors explicit 0 and falls back to the
      // max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // The policy-wide / pool rate drives the monthly cap. The DB rate is
      // surfaced as the base-pool rate slot only when it equals the pool rate
      // for the UI; the DB-specific rate has no dedicated ClaimTerms field, so
      // the presentation reads `inflationRate` for the cap and the strategy
      // owns the DB rate internally.
      inflationRate: poolRate,
      inflationCapYears: capYears,
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: poolRate,
      baseBenefitPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: faceAtIssue,
      // No EOB pool — leave the EOB inflation/at-issue knobs unset so the UI
      // does not render a phantom continuation pool.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      // When no daily/monthly cap is configured the engine sets each row's
      // effective cap to its eligible cost — the UI needs this signal to avoid
      // presenting that echo as a policy cap.
      hasConfiguredBenefitCap: hasConfiguredMonthlyCap(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { poolRate, dbRate, capYears, isSimple } = readInflation(ltcPolicy);

    const poolFactor = growthFactor(poolRate, capYears, isSimple, policyYear);
    const dbFactor = growthFactor(dbRate, capYears, isSimple, policyYear);

    const faceAtIssue = getFaceAtIssue(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * poolFactor;
    // The single pool == the LTC capacity, inflated on the pool rate.
    const inflatedPool = faceAtIssue * poolFactor;
    // The death benefit inflates on the DB rate (these products are NOT in
    // `hasFlatDeathBenefit`).
    const inflatedFace = faceAtIssue * dbFactor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // The DB inflates — surface the inflated face, not the raw at-issue face.
      effectiveFaceAmount: inflatedFace,
      // No continuation pool.
      effectiveContinuationBenefit: 0,
      // Single pool == total LTC capacity.
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedBaseMonthly,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<LifeWithRiderState, LifeWithRiderDeltas>,
  ): MonthlyEntry<LifeWithRiderState, LifeWithRiderDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate — a care month before the premium-start year pays $0,
    // full OOP. Single-life floors its policy year at 1, so this is defensive.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<LifeWithRiderState, LifeWithRiderDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's pool inflation factor). When the
    //    policy configures no daily benefit in any environment the legacy
    //    engine paid uncapped — let eligible cost set the month's effective
    //    cap instead of collapsing every payout to $0.
    const { poolRate, dbRate, capYears, isSimple } = readInflation(ltcPolicy);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const hasMonthlyCap = hasConfiguredMonthlyCap(ltcPolicy);
    const configuredMonthlyCap =
      getMonthlyBenefitAmountForEnvironment(ltcPolicy, phaseCarePlan.appliedCareEnvironment) *
      growthFactor(poolRate, capYears, isSimple, policyYear);
    const monthlyCap = hasMonthlyCap ? configuredMonthlyCap : eligibleCost;

    // 3. Anniversary inflation accrual on the single pool, gated on pool > 0.
    //    Accrued against the AT-ISSUE face (asset-based hybrid convention), so
    //    an exhausted pool stays exhausted (an inflated ceiling never reopens a
    //    closed pool). `eob` accrual is always 0.
    let baseInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      if (openState.pools.baseBenefitPool > 0) {
        const faceAtIssue = getFaceAtIssue(ltcPolicy);
        const factorDelta =
          growthFactor(poolRate, capYears, isSimple, policyYear) -
          growthFactor(poolRate, capYears, isSimple, priorYear);
        baseInflationAccrual = faceAtIssue * factorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;

    // 4. Payout: branches on indemnity vs. reimbursement (mirror MoneyGuard's
    //    indemnity convention). Single pool only — there is no second pool to
    //    fall back to.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // A configured cap is pro-rated by eligible days; a missing cap means the
    // eligible cost is already the whole row limit, so do not pro-rate again.
    const proRatedCap = hasMonthlyCap ? monthlyCap * (eligibleDays / DAYS_IN_MONTH) : monthlyCap;

    let drawFromBase = 0;
    let indemnityPaid = 0;

    if (isIndemnity && baseAfterAccrual > 0 && eligibleDays > 0) {
      // Pay the full pro-rated monthly cap as cash regardless of actual cost,
      // drawn from the single pool (the last paying month may pay less than the
      // cap when the pool can't cover it).
      const indemnityAmount = Math.min(proRatedCap, baseAfterAccrual);
      drawFromBase = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      // Reimbursement, OR an indemnity row with no funds/eligible days to pay
      // (pool exhausted or WP-only). Pay min(eligibleCost, proRatedCap) from the
      // single pool. On an exhausted indemnity row this pays $0, but the surplus
      // drain below still runs against any shortfall.
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromBase = Math.min(wouldPay, baseAfterAccrual);
    }

    // Gross cash this row pays out from the single pool. The pool depletes by
    // this gross cash; the surplus model is a separate accounting reserve.
    const rowPayout = drawFromBase;

    // 4b. Indemnity-surplus accounting (per-row), called UNCONDITIONALLY like
    //     CM II. Cost-coverage is capped at eligible cost; cash above eligible
    //     cost banks as surplus and drains on later shortfall rows WHILE the
    //     policy is still live. Once the pool is spent the reserve FREEZES (it
    //     must not bleed into post-exhaustion out-of-pocket cost). On
    //     reimbursement-election policies the open surplus is always 0 and the
    //     reimbursed payout never exceeds eligible cost, so this is a numeric
    //     no-op. WP-only / ineligible rows (eligibleCost 0) correctly never drain.
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Single pool: exhausted when the post-accrual balance can't fund a dollar.
      policyExhausted: baseAfterAccrual <= 0,
    });
    const ltcCoveragePaid = surplusResult.ltcCoveragePaid;

    // 5. Closing balances + DB reduction. The single pool's drawdown reduces
    //    the death benefit dollar-for-dollar, floored at the residual DB.
    const closeBase = baseAfterAccrual - drawFromBase;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(openState.pools.deathBenefit - drawFromBase, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const ineligibleCost =
      costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost;

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<LifeWithRiderDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      // No EOB pool — eob flow is always zero (satisfies the identity check
      // `close = open + accrual − depletion` → `0 = 0 + 0 − 0`).
      eob: { depletion: 0, inflationAccrual: 0 },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<LifeWithRiderState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: 0,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase <= 0,
      clientPoolExhausted: closeBase <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<LifeWithRiderState, LifeWithRiderDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: poolRate > 0 || dbRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<LifeWithRiderState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<LifeWithRiderState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // The residual death benefit after acceleration, already floored each month.
  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<LifeWithRiderState>): number {
    return closingState.pools.deathBenefit;
  },
};
